import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth, formatAuthError } from "../lib/AuthContext";
import { AuthShell } from "./LoginPage";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("ok");
        // Refresh the auth context so the user object reflects email_verified=true
        await refresh();
      } catch (err) {
        setStatus("error");
        setErrorMsg(formatAuthError(err.response?.data?.detail) || err.message);
      }
    })();
  }, [token, refresh]);

  if (status === "verifying") {
    return <AuthShell title="Verifying…" subtitle="Just a moment.">{null}</AuthShell>;
  }

  if (status === "ok") {
    return (
      <AuthShell title="Email confirmed" subtitle="You can now post field updates and enable push alerts.">
        <div data-testid="verify-email-ok" className="flex items-start gap-2.5 p-3 border border-tac-primary/40 bg-tac-primary/8 rounded-sm">
          <Check size={16} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div className="text-sm text-tac-text">Thanks for confirming your email.</div>
        </div>
        <div className="text-[15px] text-tac-dim text-center mt-5">
          <Link to="/" className="text-tac-primary hover:underline font-bold">Go to the live map →</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Verification failed" subtitle={errorMsg}>
      <div data-testid="verify-email-err" className="flex items-start gap-2.5 p-3 border border-tac-danger/40 bg-tac-danger/10 rounded-sm">
        <Warning size={16} weight="fill" className="text-tac-danger shrink-0 mt-0.5" />
        <div className="text-sm text-tac-text">
          Request a fresh verification email from your account page.
        </div>
      </div>
      <div className="text-[15px] text-tac-dim text-center mt-5">
        <Link to="/profile" className="text-tac-primary hover:underline font-bold">Go to account →</Link>
      </div>
    </AuthShell>
  );
}
